import React, { useEffect, useMemo, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap/lib';
import {
  ConditionFinanceType,
  CoverageNameAndDeductibleOption,
} from '@upstart/harmony/lib/legos/penTypes';
import _ from 'lodash';
import { Condition, FinanceType } from '@upstart/harmony/lib/constants';
import './CoverageConfiguration.scss';
import { FormControl, FormGroup } from 'react-bootstrap';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import classNames from 'classnames';
import { Radio } from '../../GlobalUI';
import { requiredValidationState } from '../../../util/utils';

export enum CoverageOptionKeys {
  displayName = 'displayName',
  deductible = 'deductible',
  instoreEnabled = 'instoreEnabled',
  minAge = 'minAge',
  maxAge = 'maxAge',
  minMileage = 'minMileage',
  maxMileage = 'maxMileage',
  isDefault = 'isDefault',
}

enum MinMaxValues {
  minAge = 0,
  maxAge = 100,
  minMileage = 0,
  maxMileage = 999999,
}

const isDefaultCoverageAvailable = (
  coverageOptions: CoverageNameAndDeductibleOption[],
) => {
  if (!coverageOptions.length) {
    return true;
  }

  return !!coverageOptions.find((c) => c.isDefault && c.instoreEnabled);
};

export const isCoverageOptionsValid = (
  coverageOptions: CoverageNameAndDeductibleOption[],
) => {
  if (!isDefaultCoverageAvailable(coverageOptions)) {
    return false;
  }
  for (const coverage of coverageOptions) {
    if (coverage?.instoreEnabled && !coverage.deductible) {
      return false;
    }

    if (
      coverage?.minAge &&
      (coverage.minAge < 0 ||
        coverage.minAge > (coverage.maxAge || MinMaxValues.maxAge))
    ) {
      return false;
    }
    if (
      coverage?.maxAge &&
      (coverage.maxAge < 0 || coverage.maxAge > MinMaxValues.maxAge)
    ) {
      return false;
    }

    if (
      coverage?.minMileage &&
      (coverage.minMileage < 0 ||
        coverage.minMileage > (coverage.maxMileage || MinMaxValues.maxMileage))
    ) {
      return false;
    }
    if (
      coverage?.maxMileage &&
      (coverage.maxMileage < 0 || coverage.maxMileage > MinMaxValues.maxMileage)
    ) {
      return false;
    }
  }
  return true;
};

const validateRule = (value: number | '', min: number, max: number) =>
  !value || (+value >= min && +value <= max) ? 'success' : 'error';

const CoverageConfiguration = ({
  availableCoveragesAndDeductibles,
  coverageNamesAndDeductiblesRef,
  submitted,
}: {
  availableCoveragesAndDeductibles: ConditionFinanceType<
    CoverageNameAndDeductibleOption[] | null
  >;
  coverageNamesAndDeductiblesRef: React.MutableRefObject<
    ConditionFinanceType<CoverageNameAndDeductibleOption[] | null>
  >;
  submitted: boolean;
  dealer: Dealer;
}) => {
  const [
    availableCoveragesAndDeductiblesCopy,
    setAvailableCoveragesAndDeductiblesCopy,
  ] = useState<ConditionFinanceType<CoverageNameAndDeductibleOption[] | null>>(
    availableCoveragesAndDeductibles,
  );

  useEffect(() => {
    setAvailableCoveragesAndDeductiblesCopy(availableCoveragesAndDeductibles);
  }, [availableCoveragesAndDeductibles]);

  const tabLabels = useMemo(
    () =>
      Object.keys(availableCoveragesAndDeductibles).reduce(
        (acc, condition) => {
          [FinanceType.LEASE, FinanceType.PURCHASE, FinanceType.CASH].forEach(
            (ft) => {
              const coverages = _.get(
                availableCoveragesAndDeductibles,
                `${condition}.${ft}`,
              );
              if (Array.isArray(coverages)) {
                acc.push({
                  label: `${condition}-${ft}`,
                  condition,
                  financeType: ft,
                });
              }
            },
          );
          return acc;
        },
        [] as { label: string; condition: string; financeType: FinanceType }[],
      ),
    [availableCoveragesAndDeductibles],
  );

  const handleSelectionChange = ({
    condition,
    financeType,
    index,
    field,
    value,
  }: {
    condition: Condition;
    index: number;
    financeType: FinanceType;
    field: CoverageOptionKeys;
    value: string | boolean | number;
  }) => {
    const newAvailableCoverageAndDeductible = {
      ...availableCoveragesAndDeductiblesCopy,
    } as ConditionFinanceType<CoverageNameAndDeductibleOption[]>;
    // Below logic sets the isDefault property of all the coverages to false when isDefault is set
    if (
      newAvailableCoverageAndDeductible &&
      field === CoverageOptionKeys.isDefault
    ) {
      _.set(
        newAvailableCoverageAndDeductible,
        `${condition}.${financeType}`,
        _.get(
          newAvailableCoverageAndDeductible,
          `${condition}.${financeType}`,
        ).map((c: CoverageNameAndDeductibleOption) => ({
          ...c,
          isDefault: false,
        })),
      );
    }
    if (
      [
        CoverageOptionKeys.maxAge,
        CoverageOptionKeys.minAge,
        CoverageOptionKeys.maxMileage,
        CoverageOptionKeys.minMileage,
      ].includes(field)
    ) {
      value = Number(value);
    }
    _.set(
      newAvailableCoverageAndDeductible,
      `${condition}.${financeType}[${index}].${field}`,
      value,
    );

    const coverageToUpdate: CoverageNameAndDeductibleOption = _.get(
      newAvailableCoverageAndDeductible,
      `${condition}.${financeType}[${index}]`,
    );

    // Below logic updates the default coverages when default coverage is disabled
    if (
      field === CoverageOptionKeys.instoreEnabled &&
      value === false &&
      coverageToUpdate.isDefault
    ) {
      _.set(
        newAvailableCoverageAndDeductible,
        `${condition}.${financeType}[${index}].${CoverageOptionKeys.isDefault}`,
        false,
      );
    }

    setAvailableCoveragesAndDeductiblesCopy(newAvailableCoverageAndDeductible);
    coverageNamesAndDeductiblesRef.current = newAvailableCoverageAndDeductible;
  };

  const renderData = ({
    financeType,
    condition,
  }: {
    financeType: FinanceType;
    condition: Condition;
  }) => {
    const availableCoverages: CoverageNameAndDeductibleOption[] = _.get(
      availableCoveragesAndDeductiblesCopy,
      `${condition}.${financeType}`,
    );

    if (!availableCoverages || !availableCoverages?.length) {
      return (
        <div className="CoverageConfiguration-noCoverage">
          No coverages available for {condition} - {financeType}
        </div>
      );
    }

    return (
      <table>
        <thead>
          <tr>
            <th>Default *</th>
            <th className="expanded">Imported coverage name</th>
            <th className="expanded">Coverage display name</th>
            <th className="regular">Enabled</th>
            <th className="expanded">Deductible *</th>
            <th className="regular">Min mileage</th>
            <th className="regular">Max mileage</th>
            <th className="regular">Min vehicle age</th>
            <th className="regular">Max vehicle age</th>
          </tr>
        </thead>
        <tbody>
          {availableCoverages.map(
            (coverage: CoverageNameAndDeductibleOption, index: number) => (
              <tr key={coverage.coverageName}>
                <td>
                  <FormGroup
                    controlId={`${condition}-${financeType}-isDefault-${index}`}
                  >
                    <Radio
                      containerClassName={classNames('ui-radio-container', {
                        radioError:
                          submitted &&
                          coverage.instoreEnabled &&
                          !isDefaultCoverageAvailable(availableCoverages),
                      })}
                      label="Default"
                      checked={coverage?.isDefault}
                      disabled={!coverage.instoreEnabled}
                      onChange={() =>
                        handleSelectionChange({
                          condition,
                          financeType,
                          index,
                          field: CoverageOptionKeys.isDefault,
                          value: true,
                        })
                      }
                    />
                  </FormGroup>
                </td>
                <td className="coverageNameInput">
                  <div id="coverageName">{coverage.coverageName}</div>
                </td>
                <td>
                  <FormGroup
                    controlId={`${condition}-${financeType}-coverageDisplayName-${index}`}
                  >
                    <FormControl
                      type="text"
                      className="form-control"
                      name="displayName"
                      value={coverage.displayName || ''}
                      placeholder="Label"
                      onChange={(e) =>
                        handleSelectionChange({
                          condition,
                          financeType,
                          index,
                          field: CoverageOptionKeys.displayName,
                          value: (e.target as HTMLInputElement).value,
                        })
                      }
                    />
                  </FormGroup>
                </td>
                <td className="enabledContainer">
                  <Radio
                    containerClassName=""
                    label="Yes"
                    checked={coverage.instoreEnabled}
                    onClick={() =>
                      handleSelectionChange({
                        condition,
                        financeType,
                        index,
                        field: CoverageOptionKeys.instoreEnabled,
                        value: true,
                      })
                    }
                  />
                  <Radio
                    containerClassName=""
                    label="No"
                    checked={!coverage.instoreEnabled}
                    onClick={() =>
                      handleSelectionChange({
                        condition,
                        financeType,
                        index,
                        field: CoverageOptionKeys.instoreEnabled,
                        value: false,
                      })
                    }
                  />
                </td>
                <td>
                  <FormGroup
                    controlId={`${condition}-${financeType}-deductible-${index}`}
                    validationState={requiredValidationState(
                      coverage.deductible,
                      coverage.instoreEnabled && submitted,
                    )}
                  >
                    <FormControl
                      componentClass="select"
                      value={coverage.deductible || -1}
                      required={coverage.instoreEnabled}
                      onChange={(e) =>
                        handleSelectionChange({
                          condition,
                          financeType,
                          index,
                          field: CoverageOptionKeys.deductible,
                          value: (e.target as HTMLSelectElement).value,
                        })
                      }
                      disabled={!coverage?.allDeductiblesForCoverage}
                    >
                      {!coverage?.deductible &&
                        Object.keys(coverage?.allDeductiblesForCoverage || {})
                          .length > 1 && <option value="">Select One</option>}
                      {coverage?.allDeductiblesForCoverage &&
                        Object.keys(coverage?.allDeductiblesForCoverage).map(
                          (d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ),
                        )}
                    </FormControl>
                  </FormGroup>
                </td>
                <td>
                  <FormGroup
                    controlId={`${condition}-${financeType}-minMileage-${index}`}
                    validationState={validateRule(
                      coverage.minMileage || '',
                      0,
                      coverage.maxMileage || 999999,
                    )}
                  >
                    <FormControl
                      type="number"
                      className="form-control"
                      name="minMileage"
                      max={coverage.maxMileage || MinMaxValues.maxMileage}
                      min={0}
                      value={coverage.minMileage || ''}
                      onChange={(e) =>
                        handleSelectionChange({
                          condition,
                          financeType,
                          index,
                          field: CoverageOptionKeys.minMileage,
                          value: (e.target as HTMLInputElement).value,
                        })
                      }
                    />
                  </FormGroup>
                </td>
                <td>
                  <FormGroup
                    controlId={`${condition}-${financeType}-maxMileage-${index}`}
                    validationState={validateRule(
                      coverage.maxMileage || '',
                      MinMaxValues.minMileage,
                      MinMaxValues.maxMileage,
                    )}
                  >
                    <FormControl
                      type="number"
                      className="form-control"
                      name="maxMileage"
                      max={MinMaxValues.maxMileage}
                      min={MinMaxValues.minMileage}
                      value={coverage.maxMileage || ''}
                      onChange={(e) =>
                        handleSelectionChange({
                          condition,
                          financeType,
                          index,
                          field: CoverageOptionKeys.maxMileage,
                          value: (e.target as HTMLInputElement).value,
                        })
                      }
                    />
                  </FormGroup>
                </td>
                <td>
                  <FormGroup
                    controlId={`${condition}-${financeType}-minAge-${index}`}
                    validationState={validateRule(
                      coverage.minAge || '',
                      MinMaxValues.minAge,
                      coverage.maxAge || MinMaxValues.maxAge,
                    )}
                  >
                    <FormControl
                      type="number"
                      className="form-control"
                      name="minAge"
                      max={coverage.maxAge || MinMaxValues.maxAge}
                      min={MinMaxValues.minAge}
                      value={coverage.minAge || ''}
                      onChange={(e) =>
                        handleSelectionChange({
                          condition,
                          financeType,
                          index,
                          field: CoverageOptionKeys.minAge,
                          value: (e.target as HTMLInputElement).value,
                        })
                      }
                    />
                  </FormGroup>
                </td>
                <td>
                  <FormGroup
                    controlId={`${condition}-${financeType}-maxAge-${index}`}
                    validationState={validateRule(
                      coverage.maxAge || '',
                      0,
                      100,
                    )}
                  >
                    <FormControl
                      type="number"
                      className="form-control"
                      name="maxAge"
                      max={MinMaxValues.maxAge}
                      min={MinMaxValues.minAge}
                      value={coverage.maxAge || ''}
                      onChange={(e) =>
                        handleSelectionChange({
                          condition,
                          financeType,
                          index,
                          field: CoverageOptionKeys.maxAge,
                          value: (e.target as HTMLInputElement).value,
                        })
                      }
                    />
                  </FormGroup>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    );
  };

  const renderTabs = () =>
    tabLabels.map(({ label, financeType, condition }) => {
      const isValid = !submitted
        ? true
        : isCoverageOptionsValid(
            _.get(
              availableCoveragesAndDeductiblesCopy,
              `${condition}.${financeType}`,
            ) || [],
          );
      return (
        <Tab
          tabClassName={classNames({ tabError: !isValid })}
          key={label}
          eventKey={label}
          title={`${label} ${!isValid ? '!' : ''}`}
        >
          {renderData({ financeType, condition: condition as Condition })}
        </Tab>
      );
    });

  return (
    <Tabs
      className="CoverageConfiguration"
      id="protection-plan-config-tabs"
      mountOnEnter
      unmountOnExit
    >
      {renderTabs()}
    </Tabs>
  );
};

export default React.memo(CoverageConfiguration);
